import { motion } from "framer-motion";
import { SCORE_COLORS } from "../../lib/constants";

/**
 * Animated circular score ring (0-100).
 *
 * iter110n · CEO 2026-09-21 bugfix — le composant renvoyait « 0 » quand
 * `score` était `null` / `undefined`, ce qui laissait penser que le
 * candidat avait été scoré 0/100 alors qu'il n'avait juste **pas encore
 * été analysé**. On distingue maintenant les 3 cas :
 *   • `score == null` / `undefined` → ring gris pointillé + "—/100"
 *     (l'utilisateur sait qu'il faut lancer l'analyse)
 *   • `score == 0` → affichage rouge, valeur "0" (vraie analyse basse)
 *   • `score > 0` → affichage animé habituel
 */
export default function ScoreRing({ score, size = 84, stroke = 6, label = "Score IA" }) {
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const hasScore = score !== null && score !== undefined && score !== "" && !Number.isNaN(Number(score));
    const clamped = hasScore ? Math.max(0, Math.min(100, Number(score))) : 0;
    const offset = circumference * (1 - clamped / 100);
    const { ring, text } = SCORE_COLORS(clamped);

    return (
        <div className="flex flex-col items-center gap-1.5" data-testid="score-ring" data-scored={hasScore ? "yes" : "no"}>
            <div className="relative" style={{ width: size, height: size }}>
                <svg width={size} height={size} className="-rotate-90">
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke="#F3F4F6"
                        strokeWidth={stroke}
                    />
                    {hasScore ? (
                        <motion.circle
                            cx={size / 2}
                            cy={size / 2}
                            r={radius}
                            fill="none"
                            stroke={ring}
                            strokeWidth={stroke}
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            initial={{ strokeDashoffset: circumference }}
                            animate={{ strokeDashoffset: offset }}
                            transition={{ type: "spring", stiffness: 80, damping: 18 }}
                        />
                    ) : (
                        // Ring pointillé gris pour "non scoré"
                        <circle
                            cx={size / 2}
                            cy={size / 2}
                            r={radius}
                            fill="none"
                            stroke="#D4D4D8"
                            strokeWidth={stroke}
                            strokeLinecap="round"
                            strokeDasharray="4 4"
                        />
                    )}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    {hasScore ? (
                        <>
                            <span className={`text-2xl font-extrabold tracking-tight ${text}`} data-testid="score-ring-value">
                                {Math.round(clamped)}
                            </span>
                            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">/100</span>
                        </>
                    ) : (
                        <>
                            <span className="text-2xl font-extrabold tracking-tight text-slate-300" data-testid="score-ring-value">
                                —
                            </span>
                            <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 leading-tight text-center">
                                Non<br />analysé
                            </span>
                        </>
                    )}
                </div>
            </div>
            {label ? <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</span> : null}
        </div>
    );
}
